import { useTranslation } from 'react-i18next';
import { SEOHead } from './SEOHead';
import { NavigationLink } from './Router';

export function ConsumerRightsPage() {
  const { i18n } = useTranslation();
  const isNL = i18n.language === 'nl';
  const isFR = i18n.language === 'fr';

  const content = {
    en: {
      title: "Your Rights as a Consumer: Food Poisoning in Belgium",
      subtitle: "Understanding your legal rights, compensation options, and consumer protections",
      lastUpdated: "Last updated: January 2025",
      intro: "If you've suffered from food poisoning in Belgium, you have specific rights under Belgian and European consumer protection laws. This comprehensive guide explains your rights, potential compensation, and how to protect yourself when dealing with food safety issues.",
      sections: {
        legalRights: {
          title: "Your Legal Rights Under Belgian & EU Law",
          rights: [
            {
              title: "Right to Safe Food",
              content: "Under EU Regulation 178/2002 and Belgian law, all food sold must be safe for consumption. Restaurants and food establishments have a legal obligation to ensure food safety.",
              laws: ["EU General Food Law Regulation 178/2002", "Belgian Royal Decree on Food Hygiene (KB 13/07/2014)"]
            },
            {
              title: "Right to Information",
              content: "You have the right to know about food safety risks and inspection results. Establishments must provide accurate information about allergens and food preparation.",
              laws: ["EU Food Information Regulation 1169/2011", "Belgian Food Information Act"]
            },
            {
              title: "Right to Report",
              content: "You have the right to report food safety concerns to authorities without fear of retaliation. Reports to FASFC/FAVV/AFSCA are taken seriously and investigated.",
              laws: ["Consumer Protection Act", "FASFC/FAVV/AFSCA reporting regulations"]
            },
            {
              title: "Right to Compensation",
              content: "If you suffer damages due to food poisoning, you may be entitled to compensation for medical costs, lost wages, and suffering.",
              laws: ["Belgian Civil Code Articles 1382-1383", "Product Liability Law"]
            }
          ]
        },
        compensation: {
          title: "Compensation for Food Poisoning",
          intro: "You may be entitled to various forms of compensation if you can prove the food poisoning occurred due to the establishment's negligence.",
          types: [
            {
              type: "Medical Expenses",
              details: "All medical costs directly related to the food poisoning, including doctor visits, hospital stays, medications, and follow-up treatments."
            },
            {
              type: "Lost Income",
              details: "Compensation for wages lost due to inability to work during illness. Requires documentation from employer and medical certificates."
            },
            {
              type: "Pain and Suffering",
              details: "Non-economic damages for physical pain and emotional distress caused by the food poisoning."
            },
            {
              type: "Additional Costs",
              details: "Travel expenses to medical appointments, cancelled vacation costs, childcare expenses during illness."
            }
          ],
          howTo: {
            title: "How to Claim Compensation",
            steps: [
              "Document everything: Keep all receipts, medical records, and evidence",
              "Report to FASFC/FAVV/AFSCA immediately",
              "Contact the restaurant's insurance company",
              "Consider hiring a consumer rights lawyer for serious cases",
              "File a complaint with consumer protection organizations if needed"
            ]
          }
        },
        evidence: {
          title: "Gathering Evidence for Your Case",
          intro: "Strong evidence is crucial for protecting your rights and claiming compensation.",
          checklist: [
            "Restaurant receipt showing date, time, and what was ordered",
            "Photos of the food (if possible)",
            "Medical records and doctor's diagnosis",
            "Laboratory test results confirming food poisoning",
            "Witness statements from others who ate the same food",
            "Communication with the restaurant (emails, messages)",
            "Timeline of symptoms and their severity",
            "Records of work absence and lost income"
          ]
        },
        process: {
          title: "The Legal Process",
          steps: [
            {
              step: "Initial Report",
              description: "File reports with FASFC and potentially the police for serious cases. This creates an official record.",
              timeframe: "Within 48-72 hours"
            },
            {
              step: "Medical Documentation",
              description: "Obtain comprehensive medical documentation linking your illness to the food consumed.",
              timeframe: "As soon as possible"
            },
            {
              step: "Contact Restaurant",
              description: "Formally notify the restaurant of your intention to seek compensation. Many cases are settled at this stage.",
              timeframe: "Within 1-2 weeks"
            },
            {
              step: "Insurance Claim",
              description: "File a claim with the restaurant's liability insurance. They have 3 months to respond.",
              timeframe: "Within 30 days"
            },
            {
              step: "Legal Action",
              description: "If settlement fails, consider legal action. Small claims court handles cases up to €5,000.",
              timeframe: "Within 1 year"
            }
          ]
        },
        support: {
          title: "Where to Get Help",
          organizations: [
            {
              name: "Test-Aankoop / Test-Achats",
              description: "Belgian consumer organization providing legal advice and support",
              contact: "02 542 33 33",
              website: "www.test-aankoop.be"
            },
            {
              name: "Consumer Mediation Service",
              description: "Free mediation between consumers and businesses",
              contact: "02 702 52 00",
              website: "www.consumentenombudsdienst.be"
            },
            {
              name: "European Consumer Centre Belgium",
              description: "Help with cross-border consumer issues",
              contact: "02 542 33 89",
              website: "www.eccbelgium.be"
            },
            {
              name: "Legal Aid Bureau",
              description: "Free or low-cost legal assistance for eligible individuals",
              contact: "Check local bar association",
              website: "www.avocats.be"
            }
          ]
        },
        prevention: {
          title: "Protecting Your Rights Proactively",
          tips: [
            "Always keep receipts when dining out",
            "Take photos of your meal if it looks questionable",
            "Report concerns immediately - don't wait",
            "Know the signs of food poisoning vs. other illnesses",
            "Check restaurant hygiene scores before dining",
            "Join consumer protection organizations for support",
            "Understand your insurance coverage for food poisoning"
          ]
        }
      },
      disclaimer: "This information is for educational purposes only and does not constitute legal advice. For specific legal matters, consult a qualified attorney.",
      cta: {
        report: "Report Food Poisoning Now",
        guide: "Read Complete Reporting Guide",
        check: "Check Restaurant Hygiene Scores"
      }
    },
    nl: {
      title: "Uw Rechten als Consument: Voedselvergiftiging in België",
      subtitle: "Begrijp uw wettelijke rechten, compensatiemogelijkheden en consumentenbescherming",
      lastUpdated: "Laatst bijgewerkt: januari 2025",
      intro: "Als u voedselvergiftiging heeft opgelopen in België, heeft u specifieke rechten onder Belgische en Europese consumentenbeschermingswetten. Deze uitgebreide gids legt uw rechten uit, mogelijke compensatie, en hoe u zichzelf kunt beschermen bij voedselveiligheidsproblemen.",
      sections: {
        legalRights: {
          title: "Uw Wettelijke Rechten Onder Belgisch & EU Recht",
          rights: [
            {
              title: "Recht op Veilig Voedsel",
              content: "Onder EU Verordening 178/2002 en Belgisch recht moet al het verkochte voedsel veilig zijn voor consumptie. Restaurants en voedselinstellingen hebben een wettelijke verplichting om voedselveiligheid te garanderen.",
              laws: ["EU Algemene Levensmiddelenwet Verordening 178/2002", "Belgisch Koninklijk Besluit Voedselh ygiëne (KB 13/07/2014)"]
            },
            {
              title: "Recht op Informatie",
              content: "U heeft het recht om te weten over voedselveiligheidsrisico's en inspectieresultaten. Instellingen moeten accurate informatie verstrekken over allergenen en voedselbereiding.",
              laws: ["EU Voedselinformatie Verordening 1169/2011", "Belgische Voedselinformatiewet"]
            },
            {
              title: "Recht om te Melden",
              content: "U heeft het recht om voedselveiligheidsproblemen te melden aan autoriteiten zonder angst voor represailles. Meldingen aan FAVV worden serieus genomen en onderzocht.",
              laws: ["Wet Consumentenbescherming", "FAVV meldingsreglementen"]
            },
            {
              title: "Recht op Schadevergoeding",
              content: "Als u schade lijdt door voedselvergiftiging, kunt u recht hebben op compensatie voor medische kosten, verloren loon, en leed.",
              laws: ["Belgisch Burgerlijk Wetboek Artikelen 1382-1383", "Productaansprakelijkheidswet"]
            }
          ]
        },
        compensation: {
          title: "Schadevergoeding voor Voedselvergiftiging",
          intro: "U kunt recht hebben op verschillende vormen van compensatie als u kunt bewijzen dat de voedselvergiftiging ontstond door nalatigheid van de instelling.",
          types: [
            {
              type: "Medische Kosten",
              details: "Alle medische kosten direct gerelateerd aan de voedselvergiftiging, inclusief doktersbezoeken, ziekenhuisverblijven, medicijnen, en vervolgbehandelingen."
            },
            {
              type: "Verloren Inkomen",
              details: "Compensatie voor verloren loon door onmogelijkheid om te werken tijdens ziekte. Vereist documentatie van werkgever en medische certificaten."
            },
            {
              type: "Pijn en Leed",
              details: "Niet-economische schade voor fysieke pijn en emotioneel leed veroorzaakt door de voedselvergiftiging."
            },
            {
              type: "Bijkomende Kosten",
              details: "Reiskosten naar medische afspraken, geannuleerde vakantiekosten, kinderopvangkosten tijdens ziekte."
            }
          ],
          howTo: {
            title: "Hoe Schadevergoeding Claimen",
            steps: [
              "Documenteer alles: Bewaar alle bonnetjes, medische dossiers, en bewijs",
              "Meld onmiddellijk bij FAVV",
              "Contacteer de verzekeringsmaatschappij van het restaurant",
              "Overweeg een consumentenrechten advocaat voor ernstige gevallen",
              "Dien een klacht in bij consumentenorganisaties indien nodig"
            ]
          }
        },
        evidence: {
          title: "Bewijs Verzamelen voor Uw Zaak",
          intro: "Sterk bewijs is cruciaal voor het beschermen van uw rechten en het claimen van compensatie.",
          checklist: [
            "Restaurant bonnetje met datum, tijd, en wat besteld werd",
            "Foto's van het voedsel (indien mogelijk)",
            "Medische dossiers en doktersdiagnose",
            "Laboratoriumtestresultaten die voedselvergiftiging bevestigen",
            "Getuigenverklaringen van anderen die hetzelfde voedsel aten",
            "Communicatie met het restaurant (emails, berichten)",
            "Tijdlijn van symptomen en hun ernst",
            "Registraties van werkafwezigheid en verloren inkomen"
          ]
        },
        process: {
          title: "Het Juridische Proces",
          steps: [
            {
              step: "Eerste Melding",
              description: "Dien meldingen in bij FAVV en mogelijk de politie voor ernstige gevallen. Dit creëert een officieel dossier.",
              timeframe: "Binnen 48-72 uur"
            },
            {
              step: "Medische Documentatie",
              description: "Verkrijg uitgebreide medische documentatie die uw ziekte linkt aan het geconsumeerde voedsel.",
              timeframe: "Zo snel mogelijk"
            },
            {
              step: "Contact Restaurant",
              description: "Stel het restaurant formeel op de hoogte van uw intentie om compensatie te zoeken. Veel zaken worden in deze fase geregeld.",
              timeframe: "Binnen 1-2 weken"
            },
            {
              step: "Verzekeringsclaim",
              description: "Dien een claim in bij de aansprakelijkheidsverzekering van het restaurant. Ze hebben 3 maanden om te reageren.",
              timeframe: "Binnen 30 dagen"
            },
            {
              step: "Juridische Actie",
              description: "Als schikking mislukt, overweeg juridische actie. Vredegerecht behandelt zaken tot €5.000.",
              timeframe: "Binnen 1 jaar"
            }
          ]
        },
        support: {
          title: "Waar Hulp Te Krijgen",
          organizations: [
            {
              name: "Test-Aankoop",
              description: "Belgische consumentenorganisatie die juridisch advies en ondersteuning biedt",
              contact: "02 542 33 33",
              website: "www.test-aankoop.be"
            },
            {
              name: "Consumentenombudsdienst",
              description: "Gratis bemiddeling tussen consumenten en bedrijven",
              contact: "02 702 52 00",
              website: "www.consumentenombudsdienst.be"
            },
            {
              name: "Europees Centrum voor de Consument België",
              description: "Hulp bij grensoverschrijdende consumentenkwesties",
              contact: "02 542 33 89",
              website: "www.eccbelgium.be"
            },
            {
              name: "Bureau voor Juridische Bijstand",
              description: "Gratis of goedkope juridische bijstand voor in aanmerking komende personen",
              contact: "Controleer lokale balie",
              website: "www.avocats.be"
            }
          ]
        },
        prevention: {
          title: "Uw Rechten Proactief Beschermen",
          tips: [
            "Bewaar altijd bonnetjes wanneer u uit eten gaat",
            "Maak foto's van uw maaltijd als het er twijfelachtig uitziet",
            "Meld zorgen onmiddellijk - wacht niet",
            "Ken de tekenen van voedselvergiftiging vs. andere ziektes",
            "Controleer restaurant hygiëne scores voor het dineren",
            "Word lid van consumentenbeschermingsorganisaties voor ondersteuning",
            "Begrijp uw verzekeringsdekking voor voedselvergiftiging"
          ]
        }
      },
      disclaimer: "Deze informatie is alleen voor educatieve doeleinden en vormt geen juridisch advies. Voor specifieke juridische zaken, raadpleeg een gekwalificeerde advocaat.",
      cta: {
        report: "Meld Voedselvergiftiging Nu",
        guide: "Lees Complete Meldingsgids",
        check: "Controleer Restaurant Hygiëne Scores"
      }
    },
    fr: {
      title: "Vos Droits en tant que Consommateur: Intoxication Alimentaire en Belgique",
      subtitle: "Comprendre vos droits légaux, options de compensation et protections des consommateurs",
      lastUpdated: "Dernière mise à jour: janvier 2025",
      intro: "Si vous avez souffert d'une intoxication alimentaire en Belgique, vous avez des droits spécifiques en vertu des lois belges et européennes de protection des consommateurs. Ce guide complet explique vos droits, les compensations potentielles et comment vous protéger face aux problèmes de sécurité alimentaire.",
      sections: {
        legalRights: {
          title: "Vos Droits Légaux Selon la Loi Belge et Européenne",
          rights: [
            {
              title: "Droit à une Alimentation Sûre",
              content: "Selon le Règlement UE 178/2002 et la loi belge, tous les aliments vendus doivent être sûrs pour la consommation. Les restaurants et établissements alimentaires ont l'obligation légale d'assurer la sécurité alimentaire.",
              laws: ["Règlement UE sur la Législation Alimentaire Générale 178/2002", "Arrêté Royal Belge sur l'Hygiène Alimentaire (AR 13/07/2014)"]
            },
            {
              title: "Droit à l'Information",
              content: "Vous avez le droit de connaître les risques de sécurité alimentaire et les résultats d'inspection. Les établissements doivent fournir des informations précises sur les allergènes et la préparation des aliments.",
              laws: ["Règlement UE sur l'Information Alimentaire 1169/2011", "Loi Belge sur l'Information Alimentaire"]
            },
            {
              title: "Droit de Signaler",
              content: "Vous avez le droit de signaler des problèmes de sécurité alimentaire aux autorités sans crainte de représailles. Les signalements à l'AFSCA sont pris au sérieux et font l'objet d'une enquête.",
              laws: ["Loi sur la Protection des Consommateurs", "Règlements de signalement AFSCA"]
            },
            {
              title: "Droit à une Compensation",
              content: "Si vous subissez des dommages dus à une intoxication alimentaire, vous pourriez avoir droit à une compensation pour les frais médicaux, les salaires perdus et la souffrance.",
              laws: ["Code Civil Belge Articles 1382-1383", "Loi sur la Responsabilité du Produit"]
            }
          ]
        },
        compensation: {
          title: "Compensation pour Intoxication Alimentaire",
          intro: "Vous pourriez avoir droit à diverses formes de compensation si vous pouvez prouver que l'intoxication alimentaire s'est produite en raison de la négligence de l'établissement.",
          types: [
            {
              type: "Frais Médicaux",
              details: "Tous les frais médicaux directement liés à l'intoxication alimentaire, y compris les visites chez le médecin, les séjours à l'hôpital, les médicaments et les traitements de suivi."
            },
            {
              type: "Perte de Revenus",
              details: "Compensation pour les salaires perdus en raison de l'incapacité de travailler pendant la maladie. Nécessite une documentation de l'employeur et des certificats médicaux."
            },
            {
              type: "Douleur et Souffrance",
              details: "Dommages non économiques pour la douleur physique et la détresse émotionnelle causées par l'intoxication alimentaire."
            },
            {
              type: "Coûts Supplémentaires",
              details: "Frais de déplacement pour les rendez-vous médicaux, frais de vacances annulées, frais de garde d'enfants pendant la maladie."
            }
          ],
          howTo: {
            title: "Comment Réclamer une Compensation",
            steps: [
              "Documentez tout: Gardez tous les reçus, dossiers médicaux et preuves",
              "Signalez immédiatement à l'AFSCA",
              "Contactez la compagnie d'assurance du restaurant",
              "Envisagez d'engager un avocat spécialisé en droits des consommateurs pour les cas graves",
              "Déposez une plainte auprès des organisations de protection des consommateurs si nécessaire"
            ]
          }
        },
        evidence: {
          title: "Rassembler des Preuves pour Votre Cas",
          intro: "Des preuves solides sont cruciales pour protéger vos droits et réclamer une compensation.",
          checklist: [
            "Reçu du restaurant montrant la date, l'heure et ce qui a été commandé",
            "Photos de la nourriture (si possible)",
            "Dossiers médicaux et diagnostic du médecin",
            "Résultats de tests de laboratoire confirmant l'intoxication alimentaire",
            "Déclarations de témoins d'autres personnes qui ont mangé la même nourriture",
            "Communication avec le restaurant (emails, messages)",
            "Chronologie des symptômes et leur gravité",
            "Registres d'absence du travail et de perte de revenus"
          ]
        },
        process: {
          title: "Le Processus Juridique",
          steps: [
            {
              step: "Signalement Initial",
              description: "Déposez des signalements auprès de l'AFSCA et éventuellement de la police pour les cas graves. Cela crée un dossier officiel.",
              timeframe: "Dans les 48-72 heures"
            },
            {
              step: "Documentation Médicale",
              description: "Obtenez une documentation médicale complète reliant votre maladie aux aliments consommés.",
              timeframe: "Dès que possible"
            },
            {
              step: "Contact Restaurant",
              description: "Informez formellement le restaurant de votre intention de demander une compensation. De nombreux cas sont réglés à ce stade.",
              timeframe: "Dans 1-2 semaines"
            },
            {
              step: "Réclamation d'Assurance",
              description: "Déposez une réclamation auprès de l'assurance responsabilité du restaurant. Ils ont 3 mois pour répondre.",
              timeframe: "Dans les 30 jours"
            },
            {
              step: "Action Juridique",
              description: "Si le règlement échoue, envisagez une action en justice. La justice de paix traite les affaires jusqu'à 5 000 €.",
              timeframe: "Dans l'année"
            }
          ]
        },
        support: {
          title: "Où Obtenir de l'Aide",
          organizations: [
            {
              name: "Test-Achats",
              description: "Organisation belge de consommateurs fournissant des conseils juridiques et un soutien",
              contact: "02 542 33 33",
              website: "www.test-achats.be"
            },
            {
              name: "Service de Médiation pour le Consommateur",
              description: "Médiation gratuite entre consommateurs et entreprises",
              contact: "02 702 52 00",
              website: "www.mediationconsommateur.be"
            },
            {
              name: "Centre Européen des Consommateurs Belgique",
              description: "Aide pour les problèmes de consommation transfrontaliers",
              contact: "02 542 33 89",
              website: "www.cecbelgique.be"
            },
            {
              name: "Bureau d'Aide Juridique",
              description: "Assistance juridique gratuite ou à faible coût pour les personnes éligibles",
              contact: "Vérifiez le barreau local",
              website: "www.avocats.be"
            }
          ]
        },
        prevention: {
          title: "Protéger Vos Droits de Manière Proactive",
          tips: [
            "Gardez toujours les reçus lorsque vous dînez au restaurant",
            "Prenez des photos de votre repas s'il semble douteux",
            "Signalez les préoccupations immédiatement - n'attendez pas",
            "Connaissez les signes d'intoxication alimentaire par rapport à d'autres maladies",
            "Vérifiez les scores d'hygiène des restaurants avant de dîner",
            "Rejoignez des organisations de protection des consommateurs pour obtenir du soutien",
            "Comprenez votre couverture d'assurance pour l'intoxication alimentaire"
          ]
        }
      },
      disclaimer: "Ces informations sont à des fins éducatives uniquement et ne constituent pas un avis juridique. Pour des questions juridiques spécifiques, consultez un avocat qualifié.",
      cta: {
        report: "Signaler une Intoxication Alimentaire Maintenant",
        guide: "Lire le Guide Complet de Signalement",
        check: "Vérifier les Scores d'Hygiène des Restaurants"
      }
    }
  };

  const lang = isFR ? 'fr' : (isNL ? 'nl' : 'en');
  const t = content[lang];

  return (
    <>
      <SEOHead page="consumer-rights" />
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 py-8 md:px-8">
          {/* Header */}
          <header className="mb-12 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              {t.title}
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-2">
              {t.subtitle}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t.lastUpdated}
            </p>
          </header>

          {/* Introduction */}
          <div className="mb-12 p-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-lg text-gray-700 dark:text-gray-300">
              {t.intro}
            </p>
          </div>

          {/* Legal Rights Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-semibold mb-6 text-gray-900 dark:text-white">
              {t.sections.legalRights.title}
            </h2>
            <div className="space-y-6">
              {t.sections.legalRights.rights.map((right, index) => (
                <div key={index} className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white flex items-center">
                    <span className="mr-3">⚖️</span> {right.title}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-3">{right.content}</p>
                  <div className="pl-6">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                      {lang === 'fr' ? 'Lois Applicables:' : (lang === 'nl' ? 'Toepasselijke Wetten:' : 'Applicable Laws:')}
                    </p>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                      {right.laws.map((law, idx) => (
                        <li key={idx}>• {law}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Compensation Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-semibold mb-6 text-gray-900 dark:text-white">
              {t.sections.compensation.title}
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">{t.sections.compensation.intro}</p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {t.sections.compensation.types.map((type, index) => (
                <div key={index} className="bg-green-50 dark:bg-green-900/20 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold mb-2 text-green-800 dark:text-green-200">
                    💰 {type.type}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">{type.details}</p>
                </div>
              ))}
            </div>

            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                {t.sections.compensation.howTo.title}
              </h3>
              <ol className="space-y-2">
                {t.sections.compensation.howTo.steps.map((step, index) => (
                  <li key={index} className="flex items-start">
                    <span className="font-bold text-yellow-700 dark:text-yellow-300 mr-3">{index + 1}.</span>
                    <span className="text-gray-700 dark:text-gray-300">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </section>

          {/* Evidence Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-semibold mb-6 text-gray-900 dark:text-white">
              {t.sections.evidence.title}
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">{t.sections.evidence.intro}</p>
            
            <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                📋 {lang === 'fr' ? 'Liste de Contrôle des Preuves' : (lang === 'nl' ? 'Bewijs Checklist' : 'Evidence Checklist')}
              </h3>
              <div className="grid md:grid-cols-2 gap-3">
                {t.sections.evidence.checklist.map((item, index) => (
                  <div key={index} className="flex items-start">
                    <span className="text-blue-600 dark:text-blue-400 mr-2">✓</span>
                    <span className="text-gray-700 dark:text-gray-300 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Legal Process Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-semibold mb-6 text-gray-900 dark:text-white">
              {t.sections.process.title}
            </h2>
            <div className="space-y-6">
              {t.sections.process.steps.map((step, index) => (
                <div key={index} className="relative pl-8">
                  <div className="absolute left-0 top-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {index + 1}
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg ml-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {step.step}
                      </h3>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        ⏱️ {step.timeframe}
                      </span>
                    </div>
                    <p className="text-gray-700 dark:text-gray-300">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Support Organizations Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-semibold mb-6 text-gray-900 dark:text-white">
              {t.sections.support.title}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {t.sections.support.organizations.map((org, index) => (
                <div key={index} className="bg-purple-50 dark:bg-purple-900/20 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold mb-2 text-purple-800 dark:text-purple-200">
                    {org.name}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 text-sm mb-3">{org.description}</p>
                  <div className="space-y-1 text-sm">
                    <p className="text-gray-600 dark:text-gray-400">
                      📞 {org.contact}
                    </p>
                    <p className="text-gray-600 dark:text-gray-400">
                      🌐 {org.website}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Prevention Tips Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-semibold mb-6 text-gray-900 dark:text-white">
              {t.sections.prevention.title}
            </h2>
            <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-lg">
              <ul className="space-y-3">
                {t.sections.prevention.tips.map((tip, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-600 dark:text-green-400 mr-3">💡</span>
                    <span className="text-gray-700 dark:text-gray-300">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Disclaimer */}
          <div className="mb-12 p-6 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400 italic">
              ⚠️ {t.disclaimer}
            </p>
          </div>

          {/* Call to Action */}
          <div className="text-center space-y-4">
            <NavigationLink
              to="/report-food-poisoning"
              className="inline-block px-8 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors"
            >
              {t.cta.report}
            </NavigationLink>
            <div className="space-x-4">
              <NavigationLink
                to="/ultimate-guide"
                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
              >
                {t.cta.guide}
              </NavigationLink>
              <NavigationLink
                to="/"
                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
              >
                {t.cta.check}
              </NavigationLink>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}